import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Button } from '@scio3/ui';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Static, non-interactive facsimile of the Screens page action bar, shown as the
 * calm backdrop behind the onboarding survey modal so the survey floats over
 * something that reads as the real app instead of a blank page. "Add Screen"
 * looks real but does nothing; "Add-ons" and the kebab are intentionally omitted;
 * the search box and the Favorites / filter / sort / view-toggle controls are
 * grey placeholders. The whole thing is inert (pointer-events-none + aria-hidden).
 *
 * The Screens page hides the search / filter / sort / view controls until the account has a
 * screen — paired (`payment.firstDevicePaired`) or preconfigured (`company.noOfScreens`) — in
 * BOTH welcome-screen arms, so the backdrop must match what the user lands on: Row 2 is dropped
 * until then. Mirrors `showToolbarControls` in ScreensPage; keep the two in step. (No `devices`
 * term here: this renders behind the onboarding survey, where the account-level signals are all
 * that exist and the screens list is not mounted.)
 */
export function OnboardingBackdropToolbar() {
  const { t } = useTranslation();
  const firstDevicePaired = useAuthStore((s) => s.company?.payment?.firstDevicePaired);
  const noOfScreens = useAuthStore((s) => s.company?.noOfScreens ?? 0);
  const hasEverPairedScreen = !!firstDevicePaired && firstDevicePaired !== 'not_triggered';
  const showToolbarControls = hasEverPairedScreen || noOfScreens > 0;

  return (
    <div
      aria-hidden="true"
      data-testid="onboarding-modal-backdrop"
      className="pointer-events-none flex h-full overflow-hidden bg-muted px-2 pb-2 select-none"
    >
      <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-xl bg-card">
        {/* Row 1: folder title + primary actions (Add-ons + New Folder as
            placeholder blocks, no kebab). */}
        <div className="flex items-center justify-between gap-2 px-3 pt-5 pb-2.5">
          <span className="text-base font-medium text-foreground">{t('Home')}</span>
          <div className="flex shrink-0 items-center gap-2">
            <Button tabIndex={-1}>
              <Plus className="h-4 w-4" />
              {t('Add Screen')}
            </Button>
            {/* Add-ons rendered as a placeholder block (where the button would be). */}
            <div className="h-9 w-24 rounded-md bg-muted-foreground/15" />
            {/* New Folder rendered as a placeholder block (like the row-2 controls). */}
            <div className="h-9 w-28 rounded-md bg-muted-foreground/15" />
          </div>
        </div>

        {/* Row 2: search placeholder + placeholder filter/sort/view controls. Dropped entirely
            for the welcome-screen test arm (no separator) so the backdrop is a clean action row,
            matching what a brand-new v2 user lands on. */}
        {showToolbarControls && (
          <div className="flex items-center gap-2 px-3 pt-1 pb-2.5">
            {/* Search rendered as a placeholder block (matching the other controls). */}
            <div className="h-8 min-w-0 flex-1 rounded-md bg-muted-foreground/15 max-w-xs" />
            <div className="ml-auto flex items-center gap-1">
              {/* Favorites placeholder */}
              <div className="h-8 w-24 rounded-md bg-muted-foreground/15" />
              {/* Filter placeholder */}
              <div className="h-8 w-8 rounded-md bg-muted-foreground/15" />
              {/* Sort placeholder */}
              <div className="h-8 w-8 rounded-md bg-muted-foreground/15" />
              {/* View-toggle placeholder */}
              <div className="h-8 w-20 rounded-md bg-muted-foreground/15" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
